import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Alert, SegmentedControl } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDocumentContent } from '../../../../api/agents-client/agent-client.hooks';
import { Loader } from '../../../../ui-library/loader/loader';
import { AgentCodeBlock } from '../../../flora/components/chat/agent-code-block';
import { formatJson, parseDelimitedText } from './text-document-viewer.helpers';

interface TextDocumentViewerProps {
  /** The document to show; nothing is fetched while absent. */
  documentId: string | undefined;
  /** The revision to show; defaults to the current revision. */
  revision?: number;
  /** The document's text type, which picks the renderer. */
  docType: string;
}

type ViewMode = 'rendered' | 'raw';

const VIEW_MODE_OPTIONS = [
  { value: 'rendered', label: 'Preview' },
  { value: 'raw', label: 'Raw' },
];

// Standard GitHub-flavored markdown, without the chat's single-newline breaks,
// so a file renders the way it would in any markdown viewer.
const REMARK_PLUGINS = [[remarkGfm, { singleTilde: false }]] as never[];

// Images render as links: a file (or an uploaded one) could otherwise make the
// browser fetch an arbitrary remote URL just by being previewed.
// One type scale for every file type, so switching documents never changes how
// big the text reads.
const DOCUMENT_FONT_SIZE = '14px';
const DOCUMENT_LINE_HEIGHT = '1.6';
const CODE_FONT_SIZE = '13px';
const MONOSPACE_FONT = "'SF Mono', ui-monospace, Menlo, Consolas, 'Liberation Mono', 'DejaVu Sans Mono', monospace";

const MARKDOWN_COMPONENTS: Components = {
  pre: AgentCodeBlock,
  a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
  img: ({ src, alt }) => (
    <a href={typeof src === 'string' ? src : undefined} target="_blank" rel="noopener noreferrer">
      {alt || 'image'}
    </a>
  ),
};

/**
 * Shows source text (JSON or a file's raw view) in a code block with a copy
 * button, so the exact file contents can be pasted elsewhere.
 *
 * @param {{ text: string }} props - The source text to show and copy
 * @returns {JSX.Element} The code block with its copy control
 */
function CopyableCode({ text }: { text: string }) {
  const clipboard = useClipboard({ timeout: 1500 });
  return (
    <CodeBlock>
      <CopyButton type="button" onClick={() => clipboard.copy(text)} aria-label="Copy file contents">
        <Icon
          icon={clipboard.copied ? 'material-symbols:check' : 'material-symbols:content-copy-outline'}
          width={14}
          height={14}
        />
        {clipboard.copied ? 'Copied' : 'Copy'}
      </CopyButton>
      <CodeBody>
        <code>{text}</code>
      </CodeBody>
    </CodeBlock>
  );
}

/**
 * Renders a CSV, TSV, Markdown, JSON, or plain-text document from its text:
 * markdown as formatted content, CSV/TSV as a table, JSON pretty-printed, and
 * text verbatim. Content is always rendered as text through React (markdown
 * raw HTML is not enabled), never injected as HTML or loaded in a frame.
 *
 * @param {TextDocumentViewerProps} props - The document, revision, and its text type
 * @returns {JSX.Element} The rendered document with loading and error states
 */
export function TextDocumentViewer({ documentId, revision, docType }: TextDocumentViewerProps) {
  const { content, isLoading, error, refetch } = useDocumentContent(documentId, revision);
  const [viewMode, setViewMode] = useState<ViewMode>('rendered');
  const hasRenderedView = docType === 'md' || docType === 'csv' || docType === 'tsv' || docType === 'json';
  const showRaw = hasRenderedView && viewMode === 'raw';

  const table = useMemo(() => {
    if (!content || (docType !== 'csv' && docType !== 'tsv')) return null;
    return parseDelimitedText(content.content, docType === 'tsv' ? '\t' : ',');
  }, [content, docType]);

  const json = useMemo(() => (content && docType === 'json' ? formatJson(content.content) : null), [content, docType]);

  if (isLoading) {
    return (
      <Centered>
        <Loader />
      </Centered>
    );
  }

  if (error || !content) {
    return (
      <Centered>
        <Alert color="red" title="Preview unavailable">
          This file could not be loaded. You can still download it.
          <RetryButton type="button" onClick={refetch}>
            Try again
          </RetryButton>
        </Alert>
      </Centered>
    );
  }

  const [header, ...rows] = table?.rows ?? [];
  const hiddenRows = table ? table.totalRows - table.rows.length : 0;

  return (
    <ViewerRoot>
      {/* Always present, so every file type starts at the same offset. */}
      <Toolbar>
        {hasRenderedView && (
          <SegmentedControl
            size="xs"
            data={VIEW_MODE_OPTIONS}
            value={viewMode}
            onChange={(value) => setViewMode(value as ViewMode)}
            aria-label="Choose how to show the file"
          />
        )}
      </Toolbar>
      {content.truncated && (
        <Notice role="status">This file is too large to show in full. Download it to see everything.</Notice>
      )}
      <ScrollBody data-testid="text-document-viewer">
        {showRaw ? (
          <CopyableCode text={content.content} />
        ) : docType === 'txt' ? (
          <PlainTextBody>{content.content}</PlainTextBody>
        ) : docType === 'md' ? (
          <MarkdownBody>
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
              {content.content}
            </ReactMarkdown>
          </MarkdownBody>
        ) : json ? (
          <>
            {!json.valid && <Notice role="status">This file is not valid JSON, so it is shown as-is.</Notice>}
            <CopyableCode text={json.text} />
          </>
        ) : (
          <>
            {hiddenRows > 0 && (
              <Notice role="status">
                {`Showing the first ${table?.rows.length.toLocaleString()} of ${table?.totalRows.toLocaleString()} rows. Download the file to see all of them.`}
              </Notice>
            )}
            <TableScroll>
              <StyledTable>
                {header && (
                  <thead>
                    <tr>
                      {header.map((cell, columnIndex) => (
                        // Column names may repeat; key by position.
                        <Th key={columnIndex}>{cell}</Th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {rows.map((row, rowIndex) => (
                    // Rows carry no stable identity and are never reordered.
                    <Tr key={rowIndex} $striped={rowIndex % 2 === 1}>
                      {row.map((cell, columnIndex) => (
                        <Td key={columnIndex}>{cell}</Td>
                      ))}
                    </Tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableScroll>
          </>
        )}
      </ScrollBody>
    </ViewerRoot>
  );
}

// Pinned to the containing panel (which is position: relative) rather than
// laid out in flow: in flow, wide content such as a long markdown table or code
// line would set the panel's width and push it off screen. Pinned, every file
// type gets the panel's exact size and scrolls inside it, like the PDF frame.
const ViewerRoot = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 40px;
  padding: 8px 20px 0;
  flex-shrink: 0;
`;

const ScrollBody = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  padding: 12px 20px 20px;
`;

// Plain text reads like the rest of the document: the same font, size, and
// line height as rendered markdown, wrapping long lines instead of scrolling.
const PlainTextBody = styled.pre`
  margin: 0;
  font-family: inherit;
  font-size: ${DOCUMENT_FONT_SIZE};
  line-height: ${DOCUMENT_LINE_HEIGHT};
  color: var(--ink-dark);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

// Source views (JSON and every "Raw" view) use one monospace style, sized to
// sit evenly beside the document text.
const CodeBlock = styled.div`
  position: relative;
  border-radius: 8px;
  background: var(--flora-pre-bg);
`;

const CopyButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-base);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    border-color: var(--sky-base);
    background: var(--sky-lighter);
  }
`;

const CodeBody = styled.pre`
  margin: 0;
  padding: 14px 88px 14px 16px;
  font-family: ${MONOSPACE_FONT};
  font-size: ${CODE_FONT_SIZE};
  line-height: ${DOCUMENT_LINE_HEIGHT};
  color: var(--flora-pre-text);
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  code {
    font: inherit;
    background: none;
    padding: 0;
  }
`;

const Notice = styled.div`
  margin: 8px 20px 0;
  padding: 6px 10px;
  border-radius: 6px;
  background: var(--sky-lighter);
  color: var(--ink-base);
  font-size: 12px;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 96px;
`;

const RetryButton = styled.button`
  display: block;
  margin-top: 8px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-dark);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: var(--sky-base);
    background: var(--sky-lighter);
  }
`;

const MarkdownBody = styled.div`
  max-width: 100%;
  color: var(--ink-dark);
  font-size: ${DOCUMENT_FONT_SIZE};
  line-height: ${DOCUMENT_LINE_HEIGHT};
  overflow-wrap: anywhere;

  & > :first-child {
    margin-top: 0;
  }

  h1,
  h2,
  h3,
  h4 {
    margin: 16px 0 8px;
    line-height: 1.3;
  }

  p,
  ul,
  ol,
  blockquote {
    margin: 0 0 12px;
  }

  blockquote {
    padding-left: 12px;
    border-left: 3px solid var(--sky-light);
    color: var(--ink-base);
  }

  table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
    margin: 0 0 12px;
    font-size: ${DOCUMENT_FONT_SIZE};
  }

  pre {
    font-family: ${MONOSPACE_FONT};
    font-size: ${CODE_FONT_SIZE};
    line-height: ${DOCUMENT_LINE_HEIGHT};
  }

  img {
    max-width: 100%;
  }

  th,
  td {
    padding: 6px 10px;
    border: 1px solid var(--flora-table-border);
    text-align: left;
  }

  th {
    background: var(--flora-table-header-bg);
  }
`;

// No overflow of its own: the surrounding ScrollBody scrolls both ways, so the
// sticky header pins to the panel instead of an inner scroll box.
const TableScroll = styled.div`
  display: inline-block;
  min-width: 100%;
  border: 1px solid var(--flora-table-border);
  border-radius: 8px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${DOCUMENT_FONT_SIZE};
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--base-white);
  background-image: linear-gradient(var(--flora-table-header-bg), var(--flora-table-header-bg));
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: var(--ink-dark);
  border-bottom: 1px solid var(--flora-table-border);
  white-space: nowrap;
`;

const Tr = styled.tr<{ $striped: boolean }>`
  background: ${({ $striped }) => ($striped ? 'var(--flora-table-stripe-bg)' : 'transparent')};
`;

const Td = styled.td`
  padding: 6px 12px;
  color: var(--ink-base);
  border-bottom: 1px solid var(--flora-table-border);
  overflow-wrap: anywhere;
`;
